import { useQuery } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

const GET_TRANSACTIONS_SORTED_QUERY = gql`
  query transfers($clientId: ObjectId!, $tenantId: ObjectId!) {
    transfers(
      query: { client: { _id: $clientId }, tenant: { _id: $tenantId } }
    ) {
      _id
      firstName
      middleName
      lastName
      attachments {
        name
        filePath
        fileURL
      }
      otherIndividuals
      email
      mobilePhone
      propertyAddress1
      propertyAddress2
      propertyCity
      propertyState
      propertyZipcode
      amount
      fee
      purpose
      expectedClosingDate_utc
      sellerName
      notes
      status
      disbursement
      active
      createdAt_utc
      purposeData
      completedOn_utc
      clearedOn_utc
      tenant {
        _id
        name
        address1
        address2
        city
        state
        zipcode
      }
      client {
        _id
        firstName
        lastName
        address1
        address2
        city
        state
        zipcode
        propertyAddress1
        propertyAddress2
        propertyCity
        propertyState
        propertyZipcode
      }
      transferType {
        _id
        name
        isDebit
        isCredit
        purpose
      }
      sourceBankAccount {
        _id
        bankName
        description
        accountLastFourDigits
        bankDisplayName
        active
        client {
          _id
        }
        institutionCode
        updatedAt_utc
      }
      destBankAccount {
        _id
        bankName
        description
        accountLastFourDigits
        bankDisplayName
        active
        client {
          _id
        }
        institutionCode
        updatedAt_utc
      }
      agent {
        _id
        firstName
        lastName
        email
      }
      realtor {
        _id
        firstName
        lastName
        email
      }
      updatedAt_utc
    }
  }
`;

// PAYAPP-906: Exclude vendor disbursements from the Client UI
export function useAllTransfersSorted(clientID, tenantId) {
  const shouldSkipQuery = !clientID || !tenantId;

  const id = isNil(clientID) || isEmpty(clientID) ? null : clientID;

  const query = {
    client: { _id: id },
    tenant: { _id: tenantId },
    status_not_in: ['created', 'rejected', 'awaiting approval'],
    OR: [
      { transferType: { name: 'Client Disbursement' } },
      { transferType: { name: 'Client Transfer' } },
      { transferType_is_null: true },
    ],
  };

  const { loading, error, data, refetch } = useQuery(
    GET_TRANSACTIONS_SORTED_QUERY,
    {
      variables: {
        clientId: id,
        tenantId: tenantId,
        query: query,
      },
      fetchPolicy: 'no-cache',
      skip: shouldSkipQuery, // Using skip option to conditionally skip the query
    },
  );

  if (shouldSkipQuery) {
    return { loading: false, error: null, transfers: undefined, refetch: null };
  }

  return {
    loading,
    error,
    transfers: data ? data.transfers.reverse() : undefined,
    refetch,
  };
}
